import { z } from "zod";
import { ActivityOutcomeKind } from "@common/schemas/domains/activity/activity-outcomes.schema";
import { ActivitySeries } from "@common/schemas/domains/activity/activity-timeline.schema";
import {
    ActivityFilter,
    ActorType,
    AuditChannel,
    AuditLogChangeType,
    ResourceType,
    SourcePredicate,
} from "@common/types/auditLog";
import {
    ActivityFilterSchema,
    SourcePredicateSchema,
} from "@common/contracts/auditLog.contract";
import { withPlatformTwinSourceTypes } from "@common/constants/history-source-types";
import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "../useWorkspaceUserCache";

export enum RevertedFilterValue {
    ALL = "all",
    REVERTED = "reverted",
    REGULAR = "regular",
}

interface RevertedFilterParams {
    showReverted?: boolean;
    revertedOnly?: boolean;
}

export function getRevertedFilterParams(
    filter: RevertedFilterValue | undefined,
): RevertedFilterParams {
    if (filter === RevertedFilterValue.ALL) {
        return { showReverted: true };
    }
    if (filter === RevertedFilterValue.REVERTED) {
        return { revertedOnly: true };
    }
    return {};
}

export interface HistoryFilters {
    entityIds?: number[];
    actorIds?: Array<number | null>;
    actorType?: ActorType;
    changeTypes?: AuditLogChangeType[];
    resourceTypes?: ResourceType[];
    sourceTypes?: string[];
    sourcePredicates?: SourcePredicate[];
    activityFilters?: ActivityFilter[];
    activityValues?: string[];
    processSourceTypes?: string[];
    processFields?: string[];
    fields?: string[];
    start?: Date;
    end?: Date;
    revertedFilter?: RevertedFilterValue;
    channels?: AuditChannel[];
    /** Activity timeline only: changes classified as this outcome. */
    outcome?: ActivityOutcomeKind;
    /** Activity timeline only: Updated by picks matching the timeline's series. */
    series?: ActivitySeries[];
}

/** The Updated through, Activity and Action picks, which the workspace and firm Activity pages share. */
export type HistoryChangeFilters = Pick<
    HistoryFilters,
    | "channels"
    | "activityValues"
    | "activityFilters"
    | "resourceTypes"
    | "fields"
    | "changeTypes"
    | "revertedFilter"
    | "outcome"
>;

export const historyFiltersSchema = z.object({
    entityIds: z.array(z.coerce.number()).optional(),
    actorIds: z.array(z.union([z.literal(null), z.coerce.number()])).optional(),
    actorType: z.nativeEnum(ActorType).optional(),
    changeTypes: z.array(z.nativeEnum(AuditLogChangeType)).optional(),
    resourceTypes: z.array(z.nativeEnum(ResourceType)).optional(),
    sourceTypes: z.array(z.string()).optional(),
    sourcePredicates: z.array(SourcePredicateSchema).optional(),
    activityFilters: z.array(ActivityFilterSchema).optional(),
    activityValues: z.array(z.string()).optional(),
    processSourceTypes: z
        .array(z.string())
        .transform((sourceTypes) => [
            ...new Set(withPlatformTwinSourceTypes(sourceTypes)),
        ])
        .optional(),
    processFields: z.array(z.string()).optional(),
    fields: z.array(z.string()).optional(),
    start: z.coerce.date().optional(),
    end: z.coerce.date().optional(),
    revertedFilter: z.nativeEnum(RevertedFilterValue).optional(),
    channels: z.array(z.nativeEnum(AuditChannel)).optional(),
    outcome: z.nativeEnum(ActivityOutcomeKind).optional(),
    series: z.array(z.nativeEnum(ActivitySeries)).optional(),
});

export function useHistoryPageFiltersCache(): [
    HistoryFilters | undefined,
    (value: HistoryFilters | undefined) => void,
] {
    return useWorkspaceUserCache<HistoryFilters>(
        WORKSPACE_USER_CACHE_KEYS.HISTORY_PAGE_FILTERS_CACHE_KEY,
        historyFiltersSchema,
    );
}
